import React, { useState } from 'react';
import { Download, Apple, Smartphone } from 'lucide-react';
import useInstallPrompt from '../hooks/useInstallPrompt';
import IOSInstallInstructions from './IOSInstallInstructions';

/**
 * Black / white "download the app" banner.
 * variant="full"    — full-bleed strip (Home, Browse, Contact Us)
 * variant="compact" — thin single-line strip (Login, Register)
 */
const DownloadAppBanner = ({ variant = 'full', message = 'Get Instant Notifications', className = '' }) => {
  const { canInstallNatively, isIOS, isStandalone, promptInstall } = useInstallPrompt();
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  if (isStandalone) return null; // already installed — no need to ask again

  const handleDownloadClick = async () => {
    if (canInstallNatively) {
      await promptInstall();
    } else if (isIOS) {
      setShowIOSHelp(true);
    } else {
      setShowIOSHelp(true); // desktop/unsupported browser — show the same generic guidance
    }
  };

  if (variant === 'compact') {
    return (
      <>
        <div className={`w-full bg-black text-white ${className}`}>
          <div className="px-4 py-2.5 flex items-center justify-center gap-3 text-center">
            <p className="text-xs sm:text-sm">
              Download the app for a better, faster search
            </p>
            <button
              onClick={handleDownloadClick}
              className="flex-shrink-0 flex items-center gap-1.5 border border-white rounded-full px-3 py-1 text-xs font-semibold hover:bg-white hover:text-black transition-colors"
            >
              <Download className="h-3 w-3" />
              Download
            </button>
          </div>
        </div>
        <IOSInstallInstructions open={showIOSHelp} onOpenChange={setShowIOSHelp} />
      </>
    );
  }

  return (
    <>
      <div className={`w-full bg-black text-white ${className}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 sm:py-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-semibold truncate">{message}</p>
            <p className="text-[11px] sm:text-xs text-gray-400 truncate">Explore more, right from your home screen</p>
          </div>

          <button
            onClick={handleDownloadClick}
            className="flex-shrink-0 flex items-center gap-3 bg-transparent border border-white rounded-xl pl-4 pr-3 py-2 hover:bg-white hover:text-black transition-colors group"
            data-testid="download-app-banner-btn"
          >
            <div className="text-left leading-tight">
              <div className="text-xs sm:text-sm font-bold">Download App</div>
              <div className="flex items-center gap-1 text-[10px] sm:text-[11px] text-gray-400 group-hover:text-gray-600">
                <Apple className="h-2.5 w-2.5" />
                <span>iOS</span>
                <span className="mx-0.5">&amp;</span>
                <Smartphone className="h-2.5 w-2.5" />
                <span>Android</span>
              </div>
            </div>
            <Download className="h-4 w-4 flex-shrink-0" />
          </button>
        </div>
      </div>
      <IOSInstallInstructions open={showIOSHelp} onOpenChange={setShowIOSHelp} />
    </>
  );
};

export default DownloadAppBanner;
